import { useEffect, useState } from "react";
import { StudentLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/StatusBadge";
import { TradingChartLoader } from "@/components/TradingChartLoader";
import { CheckCircle2, Circle } from "lucide-react";
import api from "@/lib/api";

export default function StudentProgress() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/progress").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <StudentLayout title="Progress"><div className="flex items-center justify-center py-16"><TradingChartLoader size="lg" text="Loading progress..." /></div></StudentLayout>;

  return (
    <StudentLayout title="Progress">
      <div className="mb-5">
        <h1 className="text-3xl font-heading font-bold tracking-tight">Learning Progress</h1>
        <p className="mt-2 text-muted-foreground">Track your module, recording, and note progress.</p>
      </div>

      <Card className="glass-card mb-6" data-testid="overall-progress-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold">Keseluruhan Progress</p>
            <span className="text-3xl font-heading font-bold text-primary">{data.overall_progress}%</span>
          </div>
          <Progress value={data.overall_progress} className="h-3" />
          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            <Stat label="Module Completed" value={`${data.modules_completed}/${data.modules_total}`} testId="stat-modul" />
            <Stat label="Recordings Available" value={`${data.recordings_available}/${data.recordings_total}`} testId="stat-rec" />
            <Stat label="Notes Available" value={`${data.notes_available}/${data.notes_total}`} testId="stat-nota" />
          </div>
          <div className="mt-6 flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Access Status:</span>
            <StatusBadge status={data.access_status} />
            <span className="text-sm text-muted-foreground ml-2">Expired: {data.access_expiry}</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="glass-card">
          <CardContent className="p-6">
            <h3 className="font-heading font-semibold flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-teal" /> Module Completed</h3>
            {data.completed_modules.length === 0 ? (
              <p className="text-sm text-muted-foreground mt-3">No modules have been marked as completed yet.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {data.completed_modules.map((m) => (
                  <li key={m.id} className="rounded-lg bg-muted/40 px-3 py-2 text-sm flex items-center justify-between" data-testid={`completed-${m.id}`}>
                    <span><span className="font-mono text-primary">{m.code}</span> · {m.title}</span>
                    <CheckCircle2 className="h-4 w-4 text-teal" />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-6">
            <h3 className="font-heading font-semibold flex items-center gap-2"><Circle className="h-4 w-4 text-muted-foreground" /> Modules Not Completed</h3>
            {data.pending_modules.length === 0 ? (
              <p className="text-sm text-muted-foreground mt-3">No modules left to complete. Congratulations!</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {data.pending_modules.map((m) => (
                  <li key={m.id} className="rounded-lg bg-muted/40 px-3 py-2 text-sm" data-testid={`pending-${m.id}`}>
                    <span className="font-mono text-primary">{m.code}</span> · {m.title}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </StudentLayout>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-xl bg-muted/40 border border-border p-4" data-testid={testId}>
      <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-heading font-bold mt-1">{value}</p>
    </div>
  );
}
